/**
 * Court-Ready PDF Export Section
 * Allows users to generate and download court-ready PDF bundles
 * with module-level selection within each document type.
 * 
 * Features:
 * - Auto-saves generated PDFs to prevent regeneration
 * - Module selection within each report type
 * - D3 chart export and embedding for court exhibits
 * - Regenerate button for fresh versions
 * - Watermark support
 */

import React, { useState, useEffect } from 'react';
import axios from '@/lib/http';
import {
  FileText,
  Download,
  Loader2,
  CheckCircle,
  Scale,
  Briefcase,
  Search,
  FileSpreadsheet,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  RefreshCw,
  Clock,
  Settings,
  Eye,
  BarChart3
} from 'lucide-react';
import { exportAllCharts, uploadChartsToBackend } from '../charts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface CourtPDFExportProps {
  reportId: string;
  claimantName?: string;
  getAuthHeader: () => { Authorization: string } | {};
}

interface Module {
  id: string;
  name: string;
  description: string;
  selected: boolean;
}

interface DocumentType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  audience: string;
  audienceColor: React.CSSProperties;
  selected: boolean;
  expanded: boolean;
  modules: Module[];
  cachedAt?: string;  // ISO timestamp if cached
  cachedUrl?: string; // URL to cached PDF
}

// Define all document types with their modules
const getInitialDocuments = (): DocumentType[] => [
  {
    id: 'court_report',
    name: 'Court-Ready Report',
    description: 'Professional calculation summary suitable for judge/jury',
    icon: <Scale className="w-5 h-5" />,
    audience: 'Judge/Jury',
    audienceColor: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
    selected: true,
    expanded: false,
    modules: [
      { id: 'cover_page', name: 'Cover Page', description: 'Title, claimant info, claim amount', selected: true },
      { id: 'toc', name: 'Table of Contents', description: 'Document navigation', selected: true },
      { id: 'executive_summary', name: 'Executive Summary', description: '1-2 page overview of claim', selected: true },
      { id: 'methodology', name: 'Methodology', description: 'BI calculation approach explanation', selected: true },
      { id: 'loss_calculation', name: 'Loss Calculation Summary', description: 'Step 1 & Step 2 breakdown', selected: true },
      { id: 'fair_settlement', name: 'Fair Settlement Analysis', description: 'Monte Carlo P50 mediation value', selected: true },
      { id: 'supporting_data', name: 'Supporting Data', description: 'Benchmark period, growth adjustments', selected: true },
      { id: 'disclaimer', name: 'Disclaimer', description: 'Professional attestation and limitations', selected: true }
    ]
  },
  {
    id: 'attorney_brief',
    name: 'Attorney Brief',
    description: 'Confidential case strategy and analysis',
    icon: <Briefcase className="w-5 h-5" />,
    audience: "Attorney's Eyes Only",
    audienceColor: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
    selected: true,
    expanded: false,
    modules: [
      { id: 'case_strength', name: 'Case Strength Assessment', description: 'STRONG/MODERATE/WEAK rating', selected: true },
      { id: 'settlement_strategy', name: 'Settlement Strategy', description: 'Opening demand, target, walk-away', selected: true },
      { id: 'unclaimed_losses', name: 'Unclaimed Losses', description: 'Additional recovery opportunities', selected: true },
      { id: 'vulnerabilities', name: 'Vulnerabilities', description: 'Weaknesses insurance will exploit', selected: true },
      { id: 'recommendations', name: 'Strategic Recommendations', description: 'Action items for attorney', selected: true }
    ]
  },
  {
    id: 'forensic_report',
    name: 'Forensic Report',
    description: 'Technical analysis for expert witness testimony',
    icon: <Search className="w-5 h-5" />,
    audience: 'Expert Witness',
    audienceColor: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
    selected: true,
    expanded: false,
    modules: [
      { id: 'validity_assessment', name: 'Claim Validity Assessment', description: 'Confidence level and status', selected: true },
      { id: 'data_quality', name: 'Data Quality Analysis', description: 'Grade, reliability, issues', selected: true },
      { id: 'findings', name: 'Key Findings', description: 'Critical, high, medium severity items', selected: true },
      { id: 'financial_impact', name: 'Financial Impact Summary', description: 'Potential adjustments', selected: true },
      { id: 'benchmark_comparison', name: 'Industry Benchmark Comparison', description: 'How metrics compare to norms', selected: true }
    ]
  },
  {
    id: 'exhibits',
    name: 'Exhibits Package',
    description: 'Charts, tables, and visual evidence',
    icon: <FileSpreadsheet className="w-5 h-5" />,
    audience: 'Court/Adjuster',
    audienceColor: { backgroundColor: 'transparent', color: 'var(--color-verified)' },
    selected: true,
    expanded: false,
    modules: [
      // Tables
      { id: 'loss_calculation_table', name: 'Exhibit A: Loss Calculation Table', description: 'Detailed calculation breakdown', selected: true },
      { id: 'benchmark_analysis', name: 'Exhibit B: Benchmark Analysis Table', description: 'Period comparison data', selected: true },
      // D3 Professional Charts (Court-Ready Visualizations)
      { id: 'd3_monte_carlo', name: 'Exhibit C: Monte Carlo Distribution', description: 'Statistical probability distribution of damages (D3 chart)', selected: true },
      { id: 'd3_expense_breakdown', name: 'Exhibit D: Expense Breakdown', description: 'Fixed vs variable costs donut chart (D3 chart)', selected: true },
      { id: 'd3_loss_timeline', name: 'Exhibit E: Loss Timeline', description: 'Monthly loss progression over closure period (D3 chart)', selected: false },
      { id: 'd3_revenue_comparison', name: 'Exhibit F: Revenue Comparison', description: 'Actual vs but-for revenue analysis (D3 chart)', selected: false }
    ]
  },
  {
    id: 'completed_pl',
    name: 'Completed P&L',
    description: 'Reconstructed profit & loss statement',
    icon: <FileText className="w-5 h-5" />,
    audience: 'All Parties',
    audienceColor: { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' },
    selected: false,
    expanded: false,
    modules: [
      { id: 'monthly_pl', name: 'Monthly P&L Tables', description: 'Revenue, COGS, payroll, net profit by month', selected: true },
      { id: 'annual_summary', name: 'Annual Summaries', description: 'Year-over-year comparison', selected: true },
      { id: 'source_reconciliation', name: 'Source Document Reconciliation', description: 'Ties back to tax returns/bank statements', selected: false }
    ]
  },
  {
    id: 'evidence_bundle',
    name: 'Evidence Index',
    description: 'Catalog of all supporting documentation',
    icon: <FolderOpen className="w-5 h-5" />,
    audience: 'Discovery',
    audienceColor: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
    selected: false,
    expanded: false,
    modules: [
      { id: 'source_docs', name: 'Source Documents List', description: 'Tax returns, P&Ls, bank statements', selected: true },
      { id: 'analysis_reports', name: 'Analysis Reports Generated', description: 'All reports in this bundle', selected: true },
      { id: 'data_sources', name: 'External Data Sources', description: 'BLS, industry benchmarks used', selected: false }
    ]
  }
];

export default function CourtPDFExportSection({
  reportId,
  claimantName,
  getAuthHeader
}: CourtPDFExportProps) {
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [watermark, setWatermark] = useState<string | null>('DRAFT');
  const [documents, setDocuments] = useState<DocumentType[]>(getInitialDocuments());
  const [cachedBundle, setCachedBundle] = useState<{ generatedAt: string; documents: string[] } | null>(null);
  const [loadingCache, setLoadingCache] = useState(false);

  // Check for cached PDFs on mount
  useEffect(() => {
    checkCachedBundle();
  }, [reportId]);

  const checkCachedBundle = async () => {
    setLoadingCache(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/download/court-ready-bundle/${reportId}/status`,
        { headers: getAuthHeader() }
      );
      if (response.data.cached) {
        setCachedBundle({
          generatedAt: response.data.generated_at,
          documents: response.data.documents
        });
      }
    } catch (err) {
      // No cached bundle - that's fine
    } finally {
      setLoadingCache(false);
    }
  };

  const toggleDocument = (docId: string) => {
    setDocuments(docs => 
      docs.map(d => d.id === docId ? { ...d, selected: !d.selected } : d)
    );
  };

  const toggleDocumentExpanded = (docId: string) => {
    setDocuments(docs => 
      docs.map(d => d.id === docId ? { ...d, expanded: !d.expanded } : d)
    );
  };

  const toggleModule = (docId: string, moduleId: string) => {
    setDocuments(docs => 
      docs.map(d => {
        if (d.id !== docId) return d;
        return {
          ...d,
          modules: d.modules.map(m => 
            m.id === moduleId ? { ...m, selected: !m.selected } : m
          )
        };
      })
    );
  };

  const selectAllModules = (docId: string, selected: boolean) => {
    setDocuments(docs => 
      docs.map(d => {
        if (d.id !== docId) return d;
        return {
          ...d,
          modules: d.modules.map(m => ({ ...m, selected }))
        };
      })
    );
  };

  const selectedDocs = documents.filter(d => d.selected);
  const selectedCount = selectedDocs.length;

  const handleGenerate = async (forceRegenerate = false) => {
    if (selectedCount === 0) {
      setError('Please select at least one document to generate');
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(false);

    try {
      // Check if any D3 charts are selected in exhibits
      const exhibitsDoc = selectedDocs.find(d => d.id === 'exhibits');
      const selectedD3Charts = exhibitsDoc?.modules
        .filter(m => m.selected && m.id.startsWith('d3_'))
        .map(m => m.id) || [];
      
      // If D3 charts are selected, export and upload them first
      if (selectedD3Charts.length > 0) {
        console.log(`📊 Exporting ${selectedD3Charts.length} D3 charts for court exhibits...`);
        
        try {
          // Export all visible D3 charts
          const charts = await exportAllCharts();
          
          if (charts.length > 0) {
            // Filter to only selected charts
            const chartNameMap: Record<string, string> = {
              'd3_monte_carlo': 'Monte_Carlo_Distribution',
              'd3_expense_breakdown': 'Expense_Breakdown',
              'd3_loss_timeline': 'Loss_Timeline',
              'd3_revenue_comparison': 'Revenue_Comparison'
            };
            
            const selectedChartNames = selectedD3Charts.map(id => chartNameMap[id]).filter(Boolean);
            const chartsToUpload = charts.filter(c => selectedChartNames.includes(c.name));
            
            if (chartsToUpload.length > 0) {
              const uploadResult = await uploadChartsToBackend(
                chartsToUpload,
                reportId,
                BACKEND_URL || ''
              );
              console.log(`✅ Uploaded ${uploadResult.uploadedCount} charts for report inclusion`);
            }
          }
        } catch (chartErr) {
          console.warn('Chart export failed, continuing with report generation:', chartErr);
          // Don't block report generation if chart export fails
        }
      }

      // Build request with module selections
      const documentsPayload = selectedDocs.map(doc => ({
        type: doc.id,
        modules: doc.modules.filter(m => m.selected).map(m => m.id)
      }));

      const response = await axios.post(
        `${BACKEND_URL}/api/v1/download/court-ready-bundle/${reportId}`,
        {
          documents: selectedDocs.map(d => d.id),
          document_modules: documentsPayload,
          watermark: watermark,
          include_fair_settlement: true,
          force_regenerate: forceRegenerate,
          save_to_cache: true,  // Auto-save for future downloads
          include_d3_charts: selectedD3Charts  // Tell backend which D3 charts to include
        },
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      // Determine file type and name
      const contentType = response.headers['content-type'];
      const isZip = contentType?.includes('zip');
      const extension = isZip ? 'zip' : 'pdf';
      const filename = `Court_Ready_Bundle_${claimantName?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.${extension}`;

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      
      // Update cache status
      setCachedBundle({
        generatedAt: new Date().toISOString(),
        documents: selectedDocs.map(d => d.id)
      });

      setTimeout(() => setSuccess(false), 5000);

    } catch (err: any) {
      console.error('PDF generation failed:', err);
      setError(err.response?.data?.detail || 'Failed to generate PDF bundle');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadCached = async () => {
    if (!cachedBundle) return;
    
    setGenerating(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/v1/download/court-ready-bundle/${reportId}/cached`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );

      const contentType = response.headers['content-type'];
      const isZip = contentType?.includes('zip');
      const extension = isZip ? 'zip' : 'pdf';
      const filename = `Court_Ready_Bundle_${claimantName?.replace(/\s+/g, '_') || 'Report'}_cached.${extension}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      // If cached download fails, generate fresh
      handleGenerate(false);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="rounded-xl shadow-sm border-2 mb-6 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
      {/* Header - Always Visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-6 flex items-center justify-between transition"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
            <FileText className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <div className="text-left">
            <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Court-Ready PDF Export</h2>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Generate professional litigation-ready documents</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {cachedBundle && (
            <span className="flex items-center gap-1 px-3 py-1 rounded-full text-sm" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>
              <CheckCircle className="w-4 h-4" />
              Cached
            </span>
          )}
          {selectedCount > 0 && (
            <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
              {selectedCount} selected
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
          ) : (
            <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-dim)' }} />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-6 pb-6 border-t" style={{ borderColor: 'var(--color-needs-human-tint-bd)' }}>
          {/* Cached Bundle Banner */}
          {cachedBundle && (
            <div className="mt-4 p-4 border rounded-lg" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                  <div>
                    <p className="font-medium" style={{ color: 'var(--color-verified)' }}>Previously Generated Bundle Available</p>
                    <p className="text-sm" style={{ color: 'var(--color-verified)' }}>
                      Generated {new Date(cachedBundle.generatedAt).toLocaleString()} • {cachedBundle.documents.length} documents
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleDownloadCached}
                    disabled={generating}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg transition"
                    style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                  >
                    <Download className="w-4 h-4" />
                    Download Cached
                  </button>
                  <button
                    onClick={() => handleGenerate(true)}
                    disabled={generating}
                    className="flex items-center gap-2 px-4 py-2 border rounded-lg transition"
                    style={{ backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-verified)', borderColor: 'var(--color-verified)' }}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Regenerate
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Document Selection with Module Expansion */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Select Documents & Customize Modules</h3>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Click <Settings className="w-4 h-4 inline" /> to customize sections</p>
            </div>

            <div className="space-y-3">
              {documents.map(doc => (
                <div
                  key={doc.id}
                  className="rounded-lg border-2 transition"
                  style={
                    doc.selected
                      ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' }
                      : { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }
                  }
                >
                  {/* Document Header */}
                  <div className="p-4 flex items-center gap-3">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={doc.selected}
                      onChange={() => toggleDocument(doc.id)}
                      className="w-5 h-5 rounded"
                      style={{ accentColor: 'var(--color-brand)' }}
                    />

                    {/* Icon */}
                    <div className="p-2 rounded-lg" style={doc.selected ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>
                      {doc.icon}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{doc.name}</span>
                        <span className="text-xs px-2 py-0.5 rounded" style={doc.audienceColor}>
                          {doc.audience}
                        </span>
                      </div>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{doc.description}</p>
                    </div>

                    {/* Module Count & Expand Button */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        {doc.modules.filter(m => m.selected).length}/{doc.modules.length} modules
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDocumentExpanded(doc.id);
                        }}
                        className="p-2 rounded-lg transition"
                        style={doc.expanded ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } : { color: 'var(--color-text-dim)' }}
                        title="Customize modules"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Module Selection (Expandable) */}
                  {doc.expanded && (
                    <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
                      <div className="pt-3 flex items-center justify-between mb-2">
                        <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Select Modules to Include:</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => selectAllModules(doc.id, true)}
                            className="text-xs hover:underline"
                            style={{ color: 'var(--color-needs-human-text)' }}
                          >
                            Select All
                          </button>
                          <span style={{ color: 'var(--color-text-ghost)' }}>|</span>
                          <button
                            onClick={() => selectAllModules(doc.id, false)}
                            className="text-xs hover:underline"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            Deselect All
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {doc.modules.map(module => (
                          <label
                            key={module.id}
                            className="flex items-start gap-2 p-2 rounded cursor-pointer transition"
                            style={module.selected ? { backgroundColor: 'var(--color-needs-human-tint)' } : undefined}
                          >
                            <input
                              type="checkbox"
                              checked={module.selected}
                              onChange={() => toggleModule(doc.id, module.id)}
                              className="mt-1 w-4 h-4 rounded"
                              style={{ accentColor: 'var(--color-brand)' }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium" style={{ color: module.selected ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)' }}>
                                  {module.name}
                                </span>
                                {module.id.startsWith('d3_') && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>
                                    <BarChart3 className="w-3 h-3" />
                                    D3
                                  </span>
                                )}
                              </div>
                              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{module.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <h3 className="font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>Options</h3>
            <div className="flex flex-wrap gap-6">
              {/* Watermark Selection */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Watermark</label>
                <select
                  value={watermark || ''}
                  onChange={(e) => setWatermark(e.target.value || null)}
                  className="px-3 py-2 border rounded-lg"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  <option value="">No Watermark (Final)</option>
                  <option value="DRAFT">DRAFT</option>
                  <option value="WORK_IN_PROGRESS">WORK IN PROGRESS</option>
                  <option value="NOT_CERTIFIED">NOT CERTIFIED</option>
                </select>
              </div>

              {/* Auto-save info */}
              <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                <Clock className="w-4 h-4" />
                <span>Generated PDFs are auto-saved for quick re-download</span>
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mt-4 p-4 border rounded-lg flex items-center gap-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              <span style={{ color: 'var(--color-needs-human-text)' }}>{error}</span>
            </div>
          )}

          {success && (
            <div className="mt-4 p-4 border rounded-lg flex items-center gap-3" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
              <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              <span style={{ color: 'var(--color-verified)' }}>PDF bundle downloaded and saved successfully!</span>
            </div>
          )}

          {/* Generate Button */}
          <div className="mt-6 flex justify-between items-center">
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {selectedCount} document{selectedCount !== 1 ? 's' : ''} •
              {selectedDocs.reduce((sum, d) => sum + d.modules.filter(m => m.selected).length, 0)} total modules
            </p>
            <button
              onClick={() => handleGenerate(false)}
              disabled={generating || selectedCount === 0}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Generate & Download
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
